// Analytics tracking utilities for Proton Health Labs
import { getAttributionData, getSourcePlatform, captureUTMParams } from './utm';
import { trackingPixels } from './pixels';

export type AnalyticsEvent = 
  | 'page_view'
  | 'product_view'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'purchase'
  | 'qr_scan'
  | 'button_click'
  | 'utm_landing';  // New event for UTM arrivals

interface EventProperties {
  [key: string]: string | number | boolean | undefined;
}

class Analytics {
  private isInitialized = false;

  init() {
    if (this.isInitialized) return;
    
    // Capture UTM params on init
    const utmData = captureUTMParams();
    if (utmData) {
      // Track UTM landing event
      this.trackUTMLanding();
    }
    
    // Initialize analytics on first call
    if (import.meta.env.DEV) console.log('[Analytics] Initialized');
    this.isInitialized = true;
  }

  /**
   * Get attribution data to include with all events
   */
  private getAttribution(): Record<string, string | undefined> {
    return getAttributionData();
  }

  trackEvent(eventName: AnalyticsEvent, properties?: EventProperties) {
    this.init();
    
    // Merge attribution data with event properties
    const attribution = this.getAttribution();
    const enrichedProperties = {
      ...properties,
      ...attribution,
    };
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.log(`[Analytics] ${eventName}`, enrichedProperties);
    }

    // Track with multiple analytics providers
    this.trackWithDataLayer(eventName, enrichedProperties);
    this.trackWithLocalStorage(eventName, enrichedProperties);
  }

  private trackWithDataLayer(eventName: AnalyticsEvent, properties?: EventProperties) {
    // Push to dataLayer for Google Analytics
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: eventName,
        ...properties,
        timestamp: new Date().toISOString(),
      });
    }
  }

  private trackWithLocalStorage(eventName: AnalyticsEvent, properties?: EventProperties) {
    // Store recent events in localStorage for debugging
    try {
      const events = JSON.parse(localStorage.getItem('analytics_events') || '[]');
      events.push({
        event: eventName,
        properties,
        timestamp: new Date().toISOString(),
      });
      
      // Keep only last 50 events
      if (events.length > 50) events.shift();
      
      localStorage.setItem('analytics_events', JSON.stringify(events));
    } catch (error) {
      console.error('[Analytics] Failed to store event', error);
    }
  }

  // Page view tracking
  trackPageView(pageName: string, path?: string) {
    this.trackEvent('page_view', {
      page_name: pageName,
      page_path: path || window.location.pathname,
      page_url: window.location.href,
    });
    
    // Fire pixel page view events
    trackingPixels.pageView();
  }

  // Product tracking
  trackProductView(productName: string, productId: string, price: number) {
    this.trackEvent('product_view', {
      product_name: productName,
      product_id: productId,
      price,
      currency: 'USD',
    });
    
    // Fire pixel view content events
    trackingPixels.viewContent(productName, productId, price);
  }

  // Cart tracking (includes full attribution)
  trackAddToCart(productName: string, quantity: number, price: number) {
    const attribution = this.getAttribution();
    this.trackEvent('add_to_cart', {
      product_name: productName,
      quantity,
      price,
      value: price * quantity,
      currency: 'USD',
      // Explicitly include key attribution for add_to_cart
      ad_platform: attribution.platform,
      ad_campaign: attribution.campaign,
    });
    
    // Fire pixel add to cart events
    trackingPixels.addToCart(productName, price, quantity);
  }

  // Checkout tracking (includes full attribution)
  trackBeginCheckout(cartValue: number, itemCount: number) {
    const attribution = this.getAttribution();
    this.trackEvent('begin_checkout', {
      value: cartValue,
      item_count: itemCount,
      currency: 'USD',
      // Explicitly include key attribution for checkout
      ad_platform: attribution.platform,
      ad_campaign: attribution.campaign,
      ad_source: attribution.source,
    });
    
    // Fire pixel initiate checkout events
    trackingPixels.initiateCheckout(cartValue, itemCount);
  }

  // Purchase tracking (CRITICAL: includes full attribution for conversion reporting)
  trackPurchase(orderId: string, revenue: number, items: number, eventID?: string) {
    const attribution = this.getAttribution();
    this.trackEvent('purchase', {
      transaction_id: orderId,
      value: revenue,
      items,
      currency: 'USD',
      // Full attribution for purchase conversions
      ad_platform: attribution.platform,
      ad_source: attribution.source,
      ad_medium: attribution.medium,
      ad_campaign: attribution.campaign,
      ad_content: attribution.content,
      ad_term: attribution.term,
      landing_page: attribution.landing_page,
      first_visit: attribution.first_visit,
    });
    
    // Fire pixel purchase events (CRITICAL for ad conversion tracking).
    // eventID dedupes against the server-side CAPI Purchase for the same order.
    trackingPixels.purchase(revenue, orderId, eventID);
  }

  // Track UTM landing (when visitor arrives from ad)
  trackUTMLanding() {
    const attribution = this.getAttribution();
    this.trackEvent('utm_landing', {
      ad_platform: attribution.platform,
      ad_source: attribution.source,
      ad_medium: attribution.medium,
      ad_campaign: attribution.campaign,
      ad_content: attribution.content,
      landing_page: attribution.landing_page,
    });
  }

  // QR code tracking
  trackQRScan(productName: string, source?: string) {
    this.trackEvent('qr_scan', {
      product_name: productName,
      source: source || 'product_label',
    });
  }

  // Button click tracking
  trackButtonClick(buttonName: string, location?: string) {
    this.trackEvent('button_click', {
      button_name: buttonName,
      location: location || window.location.pathname,
    });
  }
}

export const analytics = new Analytics();

// Auto-track page views on navigation
if (typeof window !== 'undefined') {
  // Track initial page view
  analytics.trackPageView(document.title);
  
  // Track subsequent page views (for SPA routing)
  let lastPath = window.location.pathname;
  setInterval(() => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      analytics.trackPageView(document.title);
    }
  }, 1000);
}
